import React, { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, useDragControls } from 'framer-motion';
import { X } from 'lucide-react';
import { escapeWouldClearField } from './clearOnEscape';
import { useCloseOnBack } from './backLayer/useCloseOnBack';
import './BottomSheet.css';

export const SHEET_DISMISS_DISTANCE_PX = 100;
export const SHEET_DISMISS_VELOCITY = 500;

/** Reine Entscheidung, damit die Schwelle prüfbar ist, ohne eine Geste zu simulieren. */
export function sollSchliessen({ offsetY, velocityY }) {
  return offsetY > SHEET_DISMISS_DISTANCE_PX || velocityY > SHEET_DISMISS_VELOCITY;
}

/**
 * Baustein — BottomSheet
 *
 * Modales Sheet, das von der Unterkante aufsteigt (mobile Auswahl-Flächen wie die
 * Kategorie-Auswahl des Deck-Editors). Verwendung:
 *   <BottomSheet open={open} onOpenChange={setOpen} title="Kategorie wählen">…</BottomSheet>
 * - Backdrop-Tipp, Escape und der ✕-Knopf schließen; Fokus-Trap liefert Radix.
 * - Ziehen schließt: über 100px Weg oder Schwung 500 (`sollSchliessen`), sonst federt das
 *   Sheet zurück. Die ganze Fläche ist Zieh-Zone, der Griff-Balken ist nur ihr sichtbares
 *   Zeichen. Der Drag startet manuell (`dragControls`, `dragListener={false}`) statt über
 *   framers eigenen Pointer-Listener: der setzt sonst `touch-action` auf das ganze Sheet
 *   und sperrt damit auch das Scrollen von `.bottom-sheet__body`. Ein Pointerdown außerhalb
 *   des Bodys (Kopfzeile) startet sofort; innerhalb des Bodys erst, wenn die nachfolgende
 *   Bewegung eindeutig nach unten geht UND die Liste oben steht (oder gar nichts zu scrollen
 *   ist) — sonst bekäme jeder Scroll-Versuch stattdessen das Sheet zu fassen.
 * - Textauswahl ist nur für die Dauer eines echten Zugs gesperrt (`.bottom-sheet--ziehend`,
 *   `onDragStart`/`onDragEnd`) — framers eigener Pointer-Listener hätte das automatisch
 *   erledigt, `dragListener={false}` schaltet das ab. Im Ruhezustand bleiben Titel und Liste
 *   markierbar.
 * - size 'md' (70vh, Default) | 'tall' (92vh). Das Ziehen zum Schließen startet im Body nur bei scrollTop 0 —
 *   bei einem hohen Sheet mit langem Inhalt heißt das: erst hochscrollen oder Zurück-Geste/✕.
 * - z-index: Modal-Ebene (1000/1001) — über FilterStrip-Dock (150) und CardViewer (400).
 * - Abgrenzung: DetailSidePanel ist ein nicht-modales, karten-zentriertes Panel;
 *   BottomSheet ist die modale Auswahl-Fläche.
 * - Am Handy (≤900px) schließt auch die Zurück-Geste.
 * - `outsideDismissGuard` (CSS-Selektor, optional): Radix behandelt jeden Pointerdown außerhalb
 *   von `Dialog.Content` als Backdrop-Klick und schließt — auch wenn das Ziel visuell über dem
 *   Sheet liegt, aber in einen ANDEREN Portal gerendert ist (z.B. `CardViewer`, der direkt nach
 *   `document.body` portalt statt in einen von Radix erkannten Layer). Ein Treffer dieses
 *   Selektors (`event.target.closest(selector)`) unterdrückt nur DIESES Schließen; ein echter
 *   Backdrop-Klick bleibt unberührt.
 */
export default function BottomSheet({ open, onOpenChange, title, size = 'md', outsideDismissGuard, children }) {
  useCloseOnBack(open, () => onOpenChange?.(false));

  const dragControls = useDragControls();
  const bodyRef = useRef(null);
  // Hält die Startposition eines Pointers im Body, bis die Bewegungsrichtung feststeht —
  // erst dann ist entscheidbar, ob daraus ein Sheet-Zug oder ein Scroll wird. Wird bei jedem
  // neuen Pointerdown und bei Pointerup/-cancel geleert, damit kein unentschiedener Rest eines
  // abgebrochenen Zugs (der Browser schickt „cancel" statt „up", wenn er den Touch übernimmt)
  // den nächsten Zug mit einer veralteten Startposition verwechselt.
  const pendingRef = useRef(null);
  const [ziehtGerade, setZiehtGerade] = useState(false);

  const handlePointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    pendingRef.current = null; // ein neuer Zug verwirft jeden Rest eines vorigen, unentschiedenen Zugs
    const body = bodyRef.current;
    if (!body?.contains(e.target)) {
      dragControls.start(e);
      return;
    }
    const nichtsZuScrollen = body.scrollHeight <= body.clientHeight;
    if (nichtsZuScrollen) {
      dragControls.start(e);
      return;
    }
    pendingRef.current = { startY: e.clientY, scrollTop: body.scrollTop };
  };

  const handlePointerMove = (e) => {
    const pending = pendingRef.current;
    if (!pending) return;
    const deltaY = e.clientY - pending.startY;
    if (Math.abs(deltaY) < 4) return; // Zittern ignorieren, bis die Richtung eindeutig ist
    pendingRef.current = null;
    if (pending.scrollTop <= 0 && deltaY > 0) dragControls.start(e);
  };

  const handlePointerUp = () => {
    pendingRef.current = null;
    // Netz für den Fall, dass ein abgebrochener Zug (pointercancel) framers eigenes
    // onDragEnd nicht mehr erreicht — sonst bliebe die Textauswahl dauerhaft gesperrt.
    setZiehtGerade(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="bottom-sheet__overlay" />
        <Dialog.Content
          asChild
          className="bottom-sheet"
          onEscapeKeyDown={(e) => {
            if (escapeWouldClearField()) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (outsideDismissGuard && e.target instanceof Element && e.target.closest(outsideDismissGuard)) {
              e.preventDefault();
            }
          }}
        >
          <motion.div
            className={[size === 'tall' && 'bottom-sheet--tall', ziehtGerade && 'bottom-sheet--ziehend'].filter(Boolean).join(' ') || undefined}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            // 0.15 spiegelt --duration-fast (150ms, theme.css) — als Zahl, weil framer
            // keine CSS-Custom-Property lesen kann; bei einer Token-Änderung von Hand nachziehen.
            transition={{ duration: 0.15, ease: 'easeOut' }}
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onDragStart={() => setZiehtGerade(true)}
            onDragEnd={(e, info) => {
              setZiehtGerade(false);
              if (sollSchliessen({ offsetY: info.offset.y, velocityY: info.velocity.y })) {
                onOpenChange?.(false);
              }
            }}
          >
            <div className="bottom-sheet__header">
              <div className="bottom-sheet__grip" aria-hidden />
              <Dialog.Title className="bottom-sheet__title">{title}</Dialog.Title>
              <Dialog.Description className="bottom-sheet__description--sr-only">
                {title}
              </Dialog.Description>
              <Dialog.Close asChild>
                <button type="button" className="bottom-sheet__close" aria-label="Schließen">
                  <X size={18} aria-hidden />
                </button>
              </Dialog.Close>
            </div>
            <div className="bottom-sheet__body" ref={bodyRef}>
              {children}
            </div>
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
